import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, LayoutGrid, Search, Lightbulb, Rocket, Check } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";

const MODES = {
  ad_hoc: {
    label: "Progetti ad hoc",
    icon: Compass,
    title: "Su misura, dal tuo brief.",
    desc: "Parto dalla tua esigenza e dalla tua gente: progetto da zero la soluzione creativa più efficace per i tuoi obiettivi, nella comunicazione e nella formazione.",
    points: [
      { icon: Search, text: "Ascolto e analisi del brief aziendale" },
      { icon: Lightbulb, text: "Ideazione creativa dedicata" },
      { icon: Rocket, text: "Realizzazione e accompagnamento completo" },
    ],
    testid: "approach-toggle-adhoc",
  },
  portafoglio: {
    label: "Da portafoglio",
    icon: LayoutGrid,
    title: "Format già collaudati, pronti da attivare.",
    desc: "Non tutti i progetti nascono da un foglio bianco: metto a disposizione format già progettati e collaudati nel tempo, da attivare in tempi rapidi senza rinunciare all'efficacia.",
    points: [
      { icon: Check, text: "Format già testati sul campo" },
      { icon: Check, text: "Attivazione rapida" },
      { icon: Check, text: "Qualità garantita, costi efficienti" },
    ],
    testid: "approach-toggle-portfolio",
  },
};

export default function Approach() {
  const [mode, setMode] = useState("ad_hoc");
  const active = MODES[mode];

  return (
    <section id="approccio" className="relative py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <SectionHeading
          overline="L'approccio"
          title={<>Due modi di lavorare insieme. Un solo standard.</>}
          testId="approach-title"
        />

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-zinc-400 leading-relaxed text-lg max-w-md">
              Che tu abbia bisogno di una soluzione costruita da zero o di un
              formato già pronto, il punto di partenza è sempre lo stesso: le
              esigenze concrete della tua azienda.
            </p>

            <div className="mt-10 inline-flex flex-col sm:flex-row gap-2 p-2 rounded-full border border-white/10 bg-surface">
              {Object.entries(MODES).map(([key, m]) => (
                <button
                  key={key}
                  data-testid={m.testid}
                  onClick={() => setMode(key)}
                  className={`relative inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 ${
                    mode === key
                      ? "text-obsidian"
                      : "text-zinc-400 hover:text-ink"
                  }`}
                >
                  {mode === key && (
                    <motion.span
                      layoutId="approach-pill"
                      className="absolute inset-0 bg-ember rounded-full"
                      transition={{ duration: 0.5, ease: EASE }}
                    />
                  )}
                  <span className="relative z-10">{m.label}</span>
                </button>
              ))}
            </div>
          </Reveal>

          <div className="lg:col-span-7 min-h-[380px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="rounded-2xl border border-white/10 bg-surface p-8 sm:p-12 h-full"
              >
                <active.icon className="h-10 w-10 text-ember mb-8" aria-hidden="true" />
                <h3 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-4">
                  {active.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-10 max-w-xl">
                  {active.desc}
                </p>
                <ul className="space-y-5">
                  {active.points.map((p, i) => (
                    <motion.li
                      key={p.text}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.1, duration: 0.5, ease: EASE }}
                      className="flex items-center gap-4 border-b border-white/5 pb-5 last:border-0"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ember/10 text-ember">
                        <p.icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="text-ink text-base sm:text-lg">{p.text}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
